// Tlačítko s důvodem — jen pro akci, kterou účet SMÍ, ale teď jí brání stav záznamu nebo chybějící údaj (CLAUDE.md,
// docs/04 „Neaktivní primární = --disabled-bg + důvod vedle“). Aktivní tlačítko důvod neukazuje.
// Akci, na kterou účet nemá právo (role, rozsah), tlačítko nevykreslí vůbec — rozhodnutí zadavatele 28. 9. 2026,
// potvrzeno 2. 10. 2026: kdo něco nesmí, nemusí o té funkci vědět. Právo ze serveru (`muze.*`) se předá v `pravo`:
//   smi                        → aktivní tlačítko,
//   !smi a konflikt (409, stav) → neaktivní s `pravo.duvod`,
//   !smi bez konfliktu (403)    → nevykreslí se nic.
// Výjimka `ukazatZakaz` (rozhodnutí zadavatele 27. 10. 2026, provozní deník a ČOV): i zákaz kvůli roli se vykreslí
// neaktivní s důvodem ze serveru — upozornění tam vede každého, kdo deník vidí, a bez tlačítka by nevěděl, kdo to dělá.
// Vzor: obrazovka Přihlášení (hláška vedle tlačítka Microsoft).
// Stabilní rozložení (05/2690): v patičce dialogu, zásuvky a stránky se obal rozplyne a důvod je v řádku patičky
// (CSS v komponenty.css a zasuvka.css), nikdy pod tlačítkem, které by patičku zvětšilo.
// Společný důvod bloku (rozhodnutí 31. 10. 2026, `SpolecnyDuvod.tsx`): je‑li tentýž důvod vykreslený jednou nad blokem,
// tlačítko v bloku ho neopakuje — zůstane neaktivní, důvod má v `title` a `aria-describedby` míří na ten text nahoře.
import { useId, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { jeSkryte, type Pravo } from './prava';
import { useSpolecnyDuvod } from './SpolecnyDuvod';
import './komponenty.css';

export type VariantaTlacitka =
  'primarni' | 'sekundarni' | 'nebezpecne' | 'nebezpecne-obrys' | 'textove';

interface Props extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /**
   * Právo k akci (`muze.*` ze serveru, nebo `pravoZAkce(ja.akce, …)`). Zákaz kvůli roli nebo rozsahu tlačítko
   * nevykreslí; zákaz kvůli stavu (`konflikt`) ho vykreslí neaktivní s `pravo.duvod`.
   */
  pravo?: Pravo;
  /** Proč tlačítko nejde použít (stav, chybějící údaj) — zobrazí se jen s `disabled`. */
  duvod?: string;
  varianta?: VariantaTlacitka;
  /** Důvod vedle tlačítka v řádku (výchozí pod tlačítkem). */
  vRadku?: boolean;
  /** Větší cíl (44 px) — tablet, patičky. */
  velke?: boolean;
  /**
   * Pokus o stisk neaktivního tlačítka (rozhodnutí 05/2690): tlačítko vypadá neaktivně a čte se jako neaktivní
   * (`aria-disabled`), ale klik projde a zavolá `naPokus` — obrazovka zvýrazní pole, které chybí. Akci to nespustí.
   */
  naPokus?: () => void;
  /**
   * Vlastní `id` textu důvodu — jiné prvky se na něj mohou odkázat (`aria-describedby`), aby se stejný důvod
   * nemusel opakovat u každého řádku (blok Služby: důvod zamčené zakázky jen jednou nahoře).
   */
  idDuvodu?: string;
  /**
   * Zákaz kvůli roli neskrývat, ale ukázat neaktivní s `pravo.duvod` (výjimka z 27. 10. 2026 pro ISŘ, 05/4120 b:
   * kdo akci nesmí, vidí, kdo ji dělá). Bez `pravo.duvod` je tlačítko jen neaktivní (důvod stojí jinde).
   */
  ukazatZakaz?: boolean;
  /**
   * Důvod nevykreslovat u tlačítka — volající ho ukáže jinde (s `idDuvodu`) a tlačítko se na něj odkáže
   * `aria-describedby`. Akce v úzkém sloupci tabulky (položky v Můj den): důvod pod tlačítky přes celou šířku řádku,
   * ne po slovech ve sloupci (hlášení zadavatele z ostrého provozu 31. 10. 2026). Se společným důvodem bloku
   * (`SpolecnyDuvod`) se takové tlačítko nepáruje — důvod kreslí jen volající, aby se neukázal dvakrát.
   */
  duvodJinde?: boolean;
  children: ReactNode;
}

export function TlacitkoSDuvodem({
  pravo,
  duvod: duvodStavu,
  varianta = 'primarni',
  vRadku = false,
  velke = false,
  naPokus,
  idDuvodu: vlastniIdDuvodu,
  ukazatZakaz = false,
  duvodJinde = false,
  onClick,
  disabled: neaktivniStavem,
  className,
  children,
  ...zbytek
}: Props) {
  const generovaneId = useId();
  const idDuvodu = vlastniIdDuvodu ?? generovaneId;
  const kvuliPravu = pravo !== undefined && !pravo.smi;
  const disabled = kvuliPravu || neaktivniStavem;
  const duvod = kvuliPravu ? (pravo.duvod ?? duvodStavu) : duvodStavu;
  // Tentýž důvod už stojí jednou nad blokem (stav celé zakázky) — tady se neopakuje. S `duvodJinde` ho ukazuje
  // volající sám (a tlačítko se odkazuje na jeho `idDuvodu`), blok se proto nebere v úvahu.
  const spolecny = useSpolecnyDuvod(disabled && !duvodJinde ? duvod : undefined);
  // Hooky výš, rozhodnutí o skrytí až potom (pořadí hooků se nesmí měnit mezi vykresleními).
  if (!ukazatZakaz && jeSkryte(pravo)) return null;
  const ukazatDuvod = Boolean(disabled && duvod) && spolecny === null;
  // Neaktivní s `naPokus`: v DOM zůstane aktivní, aby šlo kliknout; akce se nespustí.
  const pokus = Boolean(disabled && !kvuliPravu && naPokus);
  const tridy = [
    'tlacitko',
    `tlacitko--${varianta}`,
    velke ? 'tlacitko--velke' : '',
    pokus ? 'tlacitko--neaktivni' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={`tlacitko-s-duvodem${vRadku ? ' tlacitko-s-duvodem--radek' : ''}`}>
      <button
        type="button"
        {...zbytek}
        className={tridy}
        disabled={pokus ? undefined : disabled}
        onClick={(udalost) => {
          if (pokus) {
            udalost.preventDefault();
            naPokus?.();
            return;
          }
          onClick?.(udalost);
        }}
        aria-disabled={disabled || undefined}
        aria-describedby={
          spolecny ? spolecny.id : ukazatDuvod ? idDuvodu : zbytek['aria-describedby']
        }
        title={spolecny ? (zbytek.title ?? spolecny.text) : zbytek.title}
      >
        {children}
      </button>
      {ukazatDuvod && !duvodJinde && (
        <span id={idDuvodu} className="duvod" title={duvod}>
          {duvod}
        </span>
      )}
    </span>
  );
}
